import { Input, Text, TextArea } from "@clipboard-health/ui-components";
import { FormControl, FormHelperText, Stack } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { z } from "zod";

import { SingleBoxFileUploader } from "../SingleBoxFileUploader/SingleBoxFileUploader";

export const interviewAttendanceVerificationFormSchema = z.object({
  interviewerName: z.string().trim().min(1, "Please enter the interviewer's name and role"),
  attendanceDescription: z
    .string()
    .trim()
    .min(20, "Please provide at least 20 characters describing what happened"),
  evidenceFile: z.instanceof(File).optional(),
});

export type InterviewAttendanceVerificationFormData = z.infer<
  typeof interviewAttendanceVerificationFormSchema
>;

export function VerifyInterviewAttendanceForm() {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<InterviewAttendanceVerificationFormData>();

  return (
    <form>
      <Stack spacing={7}>
        <Stack spacing={2}>
          <Text semibold variant="h5">
            Verify your Interview Attendance
          </Text>
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
            Help us verify that you attended your interview
          </Text>
        </Stack>

        <Stack spacing={4}>
          <Text semibold>What is the name and role of the person you interviewed with?</Text>
          <FormControl fullWidth error={Boolean(errors.interviewerName)}>
            <Input
              {...register("interviewerName")}
              fullWidth
              placeholder="Jane Doe, Director of Nursing"
              error={Boolean(errors.interviewerName)}
            />
            {errors.interviewerName && (
              <FormHelperText>{errors.interviewerName.message}</FormHelperText>
            )}
          </FormControl>
        </Stack>

        <Stack spacing={4}>
          <Text semibold>What happened at the interview?</Text>
          <FormControl fullWidth error={Boolean(errors.attendanceDescription)}>
            <Controller
              control={control}
              name="attendanceDescription"
              render={({ field }) => (
                <TextArea
                  {...field}
                  fullWidth
                  rows={4}
                  placeholder="We discussed a 4x2 full time schedule and I was offered the job, with orientation on Monday"
                  error={Boolean(errors.attendanceDescription)}
                />
              )}
            />
            {errors.attendanceDescription && (
              <FormHelperText>{errors.attendanceDescription.message}</FormHelperText>
            )}
          </FormControl>
        </Stack>

        <Stack spacing={4}>
          <Text>
            Optional: Upload any evidence that you attended the interview (e.g. offer letter, photo
            of the building, etc.)
          </Text>
          <Controller
            control={control}
            name="evidenceFile"
            render={({ field }) => (
              <SingleBoxFileUploader
                selectedFile={field.value}
                onFileSelected={field.onChange}
                onFileRemoved={() => {
                  field.onChange(undefined);
                }}
              />
            )}
          />
        </Stack>
      </Stack>
    </form>
  );
}
